/**
 * This file holds the definitions and configurations for tabs and menu items.
 *
 */
import { lazy } from "react";
import { membershipTypes, roles, everyone } from "./permissionDefs";
import {
  MESSAGE_EVENT_EXPORT,
  MESSAGE_EVENT_EXPORT_AUTHORIZED_CASES,
} from "models/messages";

// We can define actions that take place at the menu and table level.
// These actions must be lazy loaded to allow the site to load.
// There is a nasty issue where components are loading before firebase is initialized.
const ExportData = lazy(() => import("UIComponents/ExportData"));

/**
 * Menu Definitions
 * named object - The property is the name of the menu item.
 *  title (required) - The title of the menu item.
 *  subtitle (required) - The subtitle of the menu item.
 *  actions (optional) - The actions component of the menu item.
 *  path (required) - The path of the menu item.
 *  minMembership (optional) - The minimum membership type required to see the menu item.
 *  minRole (optional) - The minimum role required to see the menu item.
 *  tabs (required) - named object. The tabs of the menu item.
 *    tabTitle (required) - Name of the tab.
 *    tooltip (required) - Tooltip of the tab.
 *    minMembership (optional) - The minimum membership type required to see contents of the tab.
 *    tab is still visible.
 *    minRole (optional) - The minimum role required to see the tab. see contents of the tab.
 *    tab is still visible.
 *    minMembershipMessage (optional) - Message displayed when user clicks on tab.
 *    minRoleMessage (optional) - Message displayed when user clicks on tab. *
 */
const MenuTabDefs = {
  gating: {
    title: "Enforcement",
    subtitle: "Track and manage enforcement activity across marketplaces.",
    actions: ExportData,
    actionOptions: {
      exportData: { name: "Cases", event: MESSAGE_EVENT_EXPORT },
    },
    path: "/Gating",
    defaultTabPath: "/Gating/New",
    tooltip: "Take action against unauthorized sellers.",
    minMembership: everyone, // NOT IMPLEMENTED AT PARENT LEVEL - Currently available to everyone. See permissionConstants.js.
    minRole: everyone,
    tabs: {
      new: {
        tabTitle: "New",
        tooltip: "Manage new cases from unathorized sellers.",
        minMembership: everyone,
        minRole: everyone,
      },
      openCases: {
        tabTitle: "Open Cases",
        path: "OpenCases", // Can provide path if different than tabTitle.
        tooltip: "Manage and submit cases for removal.",
      },
      pending: {
        tabTitle: "Pending",
        tooltip: "Check status of cases that have been submitted.",
      },
      closed: {
        tabTitle: "Closed",
        tooltip: "View cases that have been closed.",
      },
    },
  },
  warnings: {
    title: "Price Alerts",
    subtitle:
      "Monitor your authorized sellers for price compliance on third party marketplaces and message those in violation.",
    actions: ExportData,
    actionOptions: {
      exportData: {
        name: "Offers",
        event: MESSAGE_EVENT_EXPORT_AUTHORIZED_CASES,
        toolTipTitle: "Download CSV of all authorized offers.",
      },
    },
    path: "/alerts",
    defaultTabPath: "/alerts/AuthorizedOffers",
    tooltip: "Monitor for price compliance across marketplaces.",
    tabs: {
      new: {
        tabTitle: "Authorized Offers",
        path: "AuthorizedOffers",
        tooltip: " Monitor for price compliance across marketplaces.",
      },
      templates: {
        tabTitle: "Seller Message Templates",
        path: "SellerMessageTemplates",
        tooltip: "Manage email templates for messaging sellers.",
        minMembership: membershipTypes.trial.permissionRank,
        minMembershipMessage:
          "You must have a standard membership to view and create email templates.",
      },
    },
  },
  sellers: {
    title: "Sellers",
    subtitle: "Manage your seller information.",
    path: "/Sellers",
    defaultTabPath: "/Sellers/Unauthorized",
    tooltip: "View and manage your marketplace sellers.",
    tabs: {
      authorized: {
        tabTitle: "Authorized",
        tooltip: "Manage authorized sellers.",
      },
      unauthorized: {
        tabTitle: "Unauthorized",
        tooltip: "Manage unauthorized sellers",
      },
    },
  },
  products: {
    title: "Product Listings",
    subtitle: "Manage your product catalog.",
    path: "/Products",
    defaultTabPath: "/Products/All",
    tooltip: "View and manage the product listings monitored by Sigil.",
    tabs: {
      all: {
        tabTitle: "All",
        tooltip: "Manage and view all products.",
      },
    },
  },
  account: {
    title: "Account",
    subtitle: "Manage your account.",
    path: "/Account",
    defaultTabPath: "/Account/Profile",
    tooltip: "Manage your Account.",
    tabs: {
      profile: {
        tabTitle: "Profile",
        tooltip: "View profile settings.",
      },
      billing: {
        tabTitle: "Billing Info",
        path: "BillingInfo",
        tooltip: "View billing settings.",
      },
    },
  },
  settings: {
    title: "Settings",
    subtitle: "Manage your settings.",
    path: "/Settings",
    defaultTabPath: "/Settings/Configs",
    tooltip: "Manage your settings.",
    tabs: {
      configs: {
        tabTitle: "Configs",
        tooltip: "Manage marketplaces for scanning.",
        minRole: roles.ADMIN.permissionRank,
        minRoleMessage: "Changing these setting could disrupt your program.",
      },
    },
  },
};

export default MenuTabDefs;
